import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import classNames from 'classnames'

import Tooltip from 'components/Tooltip'
import AddOrLinkButton from 'components/AddOrLinkButton'
import ErrorsBlock from 'components/ErrorsBlock'

import { ReactComponent as Plus } from 'assets/icons/calculator/plus.svg'
import { ReactComponent as Minus } from 'assets/icons/calculator/minus.svg'
import { ReactComponent as ResetIcon } from 'assets/icons/calculator/calc-reset.svg'

import { useStores } from 'hooks/useStores'

import styles from '../styles/product_card.module.scss'

const FormProductCard = ({
  minPrice,
  maxPrice,
  multiplicity,
  inPackCount,
  minPackCount,
  packageCountText,
  unitCountText,
  packageCountHint,
  article,
  inStock,
}) => {
  const [isAdd, setIsAdd] = React.useState(false)
  const [isFetchedAdd, setIsFetchedAdd] = React.useState(false)
  const { api } = useStores()

  const BUTTON_ICON = {
    PLUS: <Plus />,
    MINUS: <Minus />
  }

  const MIN_PACKAGE_COUNT = minPackCount ?? 1
  const MIN_UNIT_COUNT = inPackCount * MIN_PACKAGE_COUNT
  
  const defaultValuesForm = {
    item_quantity: MIN_UNIT_COUNT,
    package_quantity: MIN_PACKAGE_COUNT
  }

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    formState: { errors }
  } = useForm({
    defaultValues: defaultValuesForm
  })


  const { item_quantity, package_quantity } = watch()

  const addPackageCount = () => {
    setValue('package_quantity', package_quantity + 1)
    setValue('item_quantity', inPackCount * (package_quantity + 1))
  }

  const removePackageCount = () => {
    setValue('package_quantity', package_quantity - 1)
    setValue('item_quantity', inPackCount * (package_quantity - 1))
  }

  const addUnitCount = () => {
    setValue('item_quantity', item_quantity + multiplicity)
    setValue('package_quantity', Math.ceil((item_quantity + multiplicity) / inPackCount))
  }

  const removeUnitCount = () => {
    setValue('item_quantity', item_quantity - multiplicity)
    setValue('package_quantity', Math.ceil((item_quantity - multiplicity) / inPackCount))
  }

  const handelInputUnitBlur = (e) => {
    const newPackCount = e.target.value < MIN_UNIT_COUNT ? MIN_UNIT_COUNT : Math.round(e.target.value / multiplicity) * multiplicity;

    setValue('item_quantity', newPackCount)
    setValue('package_quantity', Math.ceil(newPackCount / inPackCount))
  }

  const handelInputPackageBlur = (e) => {
    const newPackCount = Math.round(e.target.value) > MIN_PACKAGE_COUNT ? Math.round(e.target.value) : MIN_PACKAGE_COUNT;

    setValue('package_quantity', newPackCount)
    setValue('item_quantity', newPackCount * inPackCount)
  }

  const changeBasketCount = () => {
    api.cart
      .getCartItems()
      .then((response) => {
        const counters = document.querySelectorAll(".basket-counter");
        // @ts-ignore
        for (const counter of counters) {
          counter.innerText = String(response.length);
        }
      })
  };

  const onSubmit = (data) => {
    setIsFetchedAdd(true)

    api.cart
      .postCartItem({
        product_article: article,
        item_quantity: data.item_quantity,
        package_quantity: data.package_quantity,
      })
      .then(() => {
        setIsAdd(true)
        changeBasketCount()
      })
      .catch((error) => {
        setIsAdd(false)
        const { response } = error
  
        if (response === undefined) return
  
        const keys = Object.keys(response.data)
        keys.forEach((key) => {
          // @ts-ignore
          setError(key, {
            type: 'validate',
            message: response.data?.[key]?.[0]
          })
        })
      })
      .finally(() => setIsFetchedAdd(false))
  }

  const resetForm = () => {
    reset(defaultValuesForm)
  }

  useEffect(() => {
    if(isAdd){
      setIsAdd(false)
    }
  },[item_quantity, package_quantity])

  const priceText = maxPrice !== null ? `${minPrice} - ${maxPrice} ₽` : `От ${minPrice} ₽`

  return (
    <form className={styles.product_card__form} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.product_card__form_row}>
        <span className={styles.product_card__form_label}>{packageCountText}: </span>
        <div className={styles.product_card__form_counter}>
          <button
            type='button'
            className={styles.product_card__form_counter_button}
            onClick={removePackageCount}
            disabled={package_quantity <= MIN_PACKAGE_COUNT}
          >
            {BUTTON_ICON['MINUS']}
          </button>
          <input
            type="number"
            {...register('package_quantity')}
            min={MIN_PACKAGE_COUNT}
            className={styles.product_card__form_counter_input}
            onBlur={handelInputPackageBlur}
          />
          <button
            type='button'
            className={styles.product_card__form_counter_button}
            onClick={addPackageCount}
          >
            {BUTTON_ICON['PLUS']}
          </button>
        </div>
      </div>
      <ErrorsBlock errors={errors?.package_quantity?.message}  />

      <div className={styles.product_card__form_row}>
        <span className={styles.product_card__form_label}>
          <span className={styles.product_card__form_label_text}>
            {unitCountText}:
          </span>
          <Tooltip className={styles.product_card__form_tooltip} toolTipMessage={packageCountHint} />
        </span>
        <div className={styles.product_card__form_counter}>
          <button
            type='button'
            className={styles.product_card__form_counter_button}
            disabled={item_quantity <= MIN_UNIT_COUNT}
            onClick={removeUnitCount}
          >
            {BUTTON_ICON['MINUS']}
          </button>
          <input
            type="number"
            {...register('item_quantity')}
            min={MIN_UNIT_COUNT}
            onBlur={handelInputUnitBlur}
            className={styles.product_card__form_counter_input}
          />
          <button
            type='button'
            className={styles.product_card__form_counter_button}
            onClick={addUnitCount}
          >
            {BUTTON_ICON['PLUS']}
          </button>
        </div>
      </div>
      <ErrorsBlock errors={errors?.item_quantity?.message}  />

      <div className={classNames(styles.product_card__form_info, styles.in_slider)}>
        <p className={styles.product_card__in_stock_wrapper}>
          Наличие:
          <span className={classNames({
            [styles.product_card__in_stock]: true,
            [styles[`product_card__in_stock--market`]]: inStock.toLowerCase() === 'в наличии'
          })}>
            {inStock}
          </span>
        </p>
        <p className={styles.product_card__price_wrapper}>
          Цена:
          <span className={styles.product_card__price}>{priceText}</span>
        </p>
      </div>


      <div className={styles.product_card__form_buttons}>
        <button type='button'
          className={styles.product_card__form_reset}
          onClick={resetForm}
        >
          <ResetIcon  className={styles.product_card__form_reset_icon}/>
          Сбросить
        </button>
        <AddOrLinkButton
          isFullWith
          isAdd={isAdd}
          isFetchedAdd={isFetchedAdd}
        />
      </div>
    </form>
  )
}

export default FormProductCard